import { useState } from 'react';
import {
  Grid,
  Stack,
  IconButton,
  FormControl,
  OutlinedInput,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Autocomplete,
  CircularProgress,
  TextField
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MainCard from 'ui-component/cards/MainCard';
import CustomNoRowsOverlay from 'ui-component/customNoRowsOverlay';
import { useEffect } from 'react';
import { Modules } from 'store/constant';
import { get } from '../../axios/apiService';
import { generateLabel } from 'utils/commonfunction';

const Subscriptions = () => {
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [permissionMatrix, setPermissionMatrix] = useState({});
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [options, setOptions] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedOption, setSelectedOption] = useState({ id: '', label: '' });
  const [customerId, setCustomerId] = useState(null);
  const [cusPage, setCusPage] = useState(0);

  useEffect(() => {
    if (open && (!options || options?.length === 0)) {
      fetchCustomer();
    }
  }, [open]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    if (!value) {
      setRows(originalRows);
    } else {
      const filteredRows = originalRows.filter(
        (row) =>
          row.name.toLowerCase().includes(value.toLowerCase()) ||
          row.emailId.toLowerCase().includes(value.toLowerCase()) ||
          row.phoneNo.includes(value) ||
          row.planName.toLowerCase().includes(value.toLowerCase()) ||
          row.period.toLowerCase().includes(value.toLowerCase()) ||
          row.status.toLowerCase().includes(value.toLowerCase()) // Include status in search
      );
      setRows(filteredRows);
    }
  };
  useEffect(() => {
    const UserPermissions = JSON.parse(localStorage.getItem(`UserPermissions`));
    const permission = UserPermissions?.find((x) => x.moduleId === Modules.Subscriptions);
    if (permission) {
      setPermissionMatrix(permission);
    } else {
      setPermissionMatrix(null);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionHistory();
  }, [customerId, paginationModel]);

  const handleSortModelChange = (model) => {
    setSortModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleView = (row) => {
    setSelectedRow(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedRow(null);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const columns = [
    {
      field: 'orderId',
      headerName: 'Order ID',
      flex: 1,
      valueGetter: (value, row) => {
        return value ? value : 'Trial_Plan';
      }
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      flex: 1,
      valueGetter: (value, row) => {
        return formatDate(row.startDate);
      }
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      flex: 1,
      valueGetter: (value, row) => {
        return formatDate(row.endDate);
      }
    },
    { field: 'price', headerName: 'Price', flex: 1, sortable: true },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const statusColors = {
          active: 'green',
          upcoming: 'blue',
          expired: 'red'
        };

        const status = params.row.status?.toLowerCase() || 'unrecognized';
        const color = statusColors[status] || 'gray';

        return (
          <span
            style={{
              color: color,
              fontWeight: 'bold',
              textTransform: 'capitalize'
            }}
          >
            {status}
          </span>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      renderCell: (params) => (
        <IconButton color="primary" onClick={() => handleView(params.row)}>
          <VisibilityIcon />
        </IconButton>
      ),
      align: 'center',
      width: 100,
      sortable: false,
      filterable: false
    }
  ];

  const fetchSubscriptionHistory = async () => {
    setLoading(true);
    try {
      const { pageSize, page } = paginationModel;
      const baseQuery = `CustomerPlanAssignment?$skip=${page * pageSize}&$top=${pageSize}&$customerId=${customerId}`;

      const sortField = sortOrderHistoryFiled(sortModel.length ? sortModel[0].field : '');
      const sortOrder = sortModel.length ? (sortModel[0].sort === 'asc' ? 'Ascending' : 'Descending') : '';
      const sortQuery =
        sortField !== '' ? `&$orderby=${sortField}&$orderbydirection=${sortOrder}` : '&$orderby=EndDate&$orderbydirection=Descending';

      const query = `${baseQuery}${sortQuery}`;
      console.log('query,', query);
      const response = await get(query);
      if (response != null) {
        setRows(response.values);
        setRowCount(response.totalCount);
      }
    } catch (err) {
      console.error(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      let response = await get(`Customer?page=${cusPage}&pageSize=999999`);
      const customerData = response?.values?.map((x) => {
        return {
          id: x.id,
          label: x?.emailId
        };
      });

      if (options?.length > 0) {
        setOptions((prevOptions) => [...prevOptions, ...customerData]);
      } else {
        setOptions(customerData);
      }
      setLoading(false);

      const totalCount = response.totalCount;
      const hasMoreData = cusPage * 100 + customerData.length < totalCount;

      if (hasMoreData) {
        setCusPage(cusPage + 1);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  const handleCustomerChange = (event, newValue) => {
    if (newValue) {
      setSelectedOption(newValue);
      setCustomerId(newValue.id);
    } else {
      setSelectedOption({ id: '', label: '' });
      setCustomerId(null);
    }
  };
  return (
    <MainCard title="Subscriptions">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
            <FormControl sx={{ width: '25ch' }} size="small">
              {/* <OutlinedInput
                placeholder="Search"
                value={search}
                onChange={handleSearchChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'grey' },
                    '&:hover fieldset': { borderColor: 'black' },
                    '&.Mui-focused fieldset': { borderColor: 'blue' }
                  }
                }}
                endAdornment={
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                }
              /> */}
            </FormControl>
            <Autocomplete
              size="small"
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              loading={loading}
              options={options || []}
              // getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={handleCustomerChange}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select a customer"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
              style={{ width: 200 }}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            getRowId={(row) => row.id}
            rows={rows}
            rowCount={rowCount}
            columns={columns}
            paginationMode="server"
            sortingMode="server"
            pageSizeOptions={[10]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            components={{
              NoRowsOverlay: CustomNoRowsOverlay
            }}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
          />
        </Grid>
      </Grid>

      {/* Modal Popup */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md" // Increase the width to 'md' (medium size)
        fullWidth={true} // Make the modal take the full width
      >
        <DialogTitle>Subscription Details</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Plan Name</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedRow && (
                  <TableRow>
                    <TableCell>{selectedRow.planName}</TableCell>
                    <TableCell>{selectedRow.period}</TableCell>
                    <TableCell>{selectedRow.status}</TableCell>
                    <TableCell>{selectedRow.startDate}</TableCell>
                    <TableCell>{selectedRow.endDate}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </MainCard>
  );
};

export default Subscriptions;
const sortOrderHistoryFiled = (filed) => {
  if (filed == 'orderId') {
    return 'orderId';
  } else if (filed == 'startDate') {
    return 'startDate';
  } else if (filed == 'endDate') {
    return 'endDate';
  } else if (filed == 'price') {
    return 'plan.price';
  }
  return '';
};
